import React from "react";

const cleanText = (value) => String(value ?? "").trim();

const numericOrder = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 9999;
};

export const isIspRegistrationAvailable = (
  value,
  registrationTypes = [],
) => {
  const selected = cleanText(value);
  if (!selected) return false;
  return (Array.isArray(registrationTypes) ? registrationTypes : []).some(
    (registration) => cleanText(registration?.name) === selected,
  );
};

export const getIspRegistrationOptionGroups = (
  registrationTypes = [],
  registrationCategoryTypes,
  selectedValue = "",
) => {
  const grouped = new Map();
  (Array.isArray(registrationTypes) ? registrationTypes : []).forEach(
    (registration, sourceIndex) => {
      const name = cleanText(registration?.name);
      if (!name) return;
      const category = cleanText(registration?.category) || "Other";
      if (!grouped.has(category)) {
        grouped.set(category, {
          label: category,
          sourceIndex,
          options: [],
        });
      }
      grouped.get(category).options.push({
        ...registration,
        name,
        sourceIndex,
        unavailable: false,
      });
    },
  );

  grouped.forEach((group) => {
    group.options.sort((left, right) => {
      const categoryOrderDifference =
        numericOrder(left.categoryOrder) - numericOrder(right.categoryOrder);
      if (categoryOrderDifference !== 0) return categoryOrderDifference;
      const orderDifference =
        numericOrder(left.order) - numericOrder(right.order);
      if (orderDifference !== 0) return orderDifference;
      return left.name.localeCompare(right.name);
    });
  });

  const groups = Array.from(grouped.values());
  if (Array.isArray(registrationCategoryTypes)) {
    const categoryOrder = new Map(
      registrationCategoryTypes.map((category) => [
        cleanText(category?.category),
        numericOrder(category?.order),
      ]),
    );
    groups.sort((left, right) => {
      const orderDifference =
        (categoryOrder.get(left.label) ?? 9999) -
        (categoryOrder.get(right.label) ?? 9999);
      return orderDifference || left.sourceIndex - right.sourceIndex;
    });
  } else {
    groups.sort((left, right) => {
      if (left.label === "Other") return 1;
      if (right.label === "Other") return -1;
      return left.label.localeCompare(right.label);
    });
  }

  const selected = cleanText(selectedValue);
  if (selected && !isIspRegistrationAvailable(selected, registrationTypes)) {
    groups.unshift({
      label: "Unavailable",
      sourceIndex: -1,
      options: [
        {
          name: selected,
          unavailable: true,
          sourceIndex: -1,
        },
      ],
    });
  }

  return groups;
};

export const IspRegistrationOptions = ({
  registrationTypes = [],
  registrationCategoryTypes,
  selectedValue = "",
}) => (
  <>
    <option value="">Select Registration</option>
    {getIspRegistrationOptionGroups(
      registrationTypes,
      registrationCategoryTypes,
      selectedValue,
    ).map((group) => (
      <optgroup label={group.label} key={group.label}>
        {group.options.map((registration, index) => (
          <option
            key={`${group.label}-${registration.name}-${index}`}
            value={registration.name}
            disabled={registration.unavailable}
          >
            {registration.name}
            {registration.unavailable ? " (Unavailable)" : ""}
          </option>
        ))}
      </optgroup>
    ))}
  </>
);

export const IspRegistrationSelect = ({
  id,
  name = id,
  value = "",
  registrationTypes = [],
  registrationCategoryTypes,
  onChange,
  emptyBorderColor = "#ced4da",
}) => {
  const available = isIspRegistrationAvailable(value, registrationTypes);
  return (
    <div>
      <div className="form-group">
        <label htmlFor={id}>
          <strong>Registration</strong>
        </label>
        <select
          name={name}
          id={id}
          className="form-control"
          style={{
            borderLeftColor: value && available ? "green" : emptyBorderColor,
            borderLeftWidth: "5px",
          }}
          onChange={onChange}
          value={value || ""}
        >
          <IspRegistrationOptions
            registrationTypes={registrationTypes}
            registrationCategoryTypes={registrationCategoryTypes}
            selectedValue={value}
          />
        </select>
      </div>
    </div>
  );
};
